import type { StoreRecord } from '../services/storeService'
import type { PickupLocationRow } from '../services/commerceService'

export type OnboardingStepId =
  | 'storeProfile'
  | 'logoBanner'
  | 'firstCategory'
  | 'firstProduct'
  | 'paymentGateway'
  | 'fulfillment'
  | 'previewStorefront'

export type OnboardingStepDef = {
  id: OnboardingStepId
  title: string
  description: string
  href?: string
  external?: boolean
  /** Platform-owned; does not block checklist dismiss. */
  optional?: boolean
}

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  {
    id: 'storeProfile',
    title: 'Complete store profile',
    description: 'Add a tagline, about text, and contact details customers will see.',
    href: '/settings',
  },
  {
    id: 'logoBanner',
    title: 'Upload logo and banner',
    description: 'Brand your storefront with a logo and About page banner.',
    href: '/settings',
  },
  {
    id: 'firstCategory',
    title: 'Create your first category',
    description: 'Organize products so shoppers can browse your catalog.',
    href: '/categories',
  },
  {
    id: 'firstProduct',
    title: 'Add your first product',
    description: 'Publish at least one product to start selling.',
    href: '/products/new',
  },
  {
    id: 'paymentGateway',
    title: 'Online payments',
    description:
      'Optional — Kartly enables card payments for your store currency. You can review Accept online payments under Payments anytime.',
    href: '/commerce',
    optional: true,
  },
  {
    id: 'fulfillment',
    title: 'Set up pickup or delivery',
    description: 'Configure how customers receive orders — add locations, then enable pickup under Payments.',
    href: '/pickup-locations',
  },
  {
    id: 'previewStorefront',
    title: 'Preview your storefront',
    description: 'Open your live customer site and confirm everything looks right.',
    external: true,
  },
]

export type OnboardingCompletionInput = {
  store: StoreRecord
  categoryTotal: number
  productTotal: number
  pickupCount: number
}

export function isOnboardingStepComplete(
  stepId: OnboardingStepId,
  input: OnboardingCompletionInput,
): boolean {
  const { store, categoryTotal, productTotal, pickupCount } = input

  switch (stepId) {
    case 'storeProfile':
      return Boolean(
        String(store.tagline ?? '').trim() ||
          String(store.aboutText ?? '').trim() ||
          String(store.contactEmail ?? '').trim(),
      )
    case 'logoBanner':
      return Boolean(String(store.logo ?? '').trim() && String(store.aboutBannerUrl ?? '').trim())
    case 'firstCategory':
      return categoryTotal > 0
    case 'firstProduct':
      return productTotal > 0
    case 'paymentGateway':
      return store.onlinePaymentsEnabled !== false && store.onlinePaymentsAvailable === true
    case 'fulfillment':
      return Boolean(
        store.payOnDeliveryEnabled ||
          store.fulfillmentDeliveryEnabled ||
          (store.fulfillmentPickupEnabled && pickupCount > 0),
      )
    case 'previewStorefront':
      return Boolean(store.onboardingStorefrontPreviewedAt)
    default:
      return false
  }
}

export function computeOnboardingProgress(input: OnboardingCompletionInput) {
  const steps = ONBOARDING_STEPS.map((step) => ({
    ...step,
    complete: isOnboardingStepComplete(step.id, input),
  }))
  const requiredSteps = steps.filter((s) => !s.optional)
  const completedRequiredCount = requiredSteps.filter((s) => s.complete).length
  const requiredTotalCount = requiredSteps.length
  const completedCount = steps.filter((s) => s.complete).length
  return {
    steps,
    /** All steps including optional (for display if needed). */
    completedCount,
    totalCount: steps.length,
    completedRequiredCount,
    requiredTotalCount,
    /** True when every merchant-owned step is done (optional online payments ignored). */
    allComplete: completedRequiredCount === requiredTotalCount,
  }
}

export function shouldShowOnboardingChecklist(
  store: StoreRecord | null | undefined,
  role: string | undefined,
): boolean {
  if (role !== 'org_admin' || !store) return false
  if (!store.onboardingChecklistDismissedAt) return true
  return false
}
